import React, { useMemo, useState } from "react";
import {
  FEDERAL_POVERTY_GUIDELINES,
  FEDERAL_POVERTY_GUIDELINE_YEAR,
  getPovertyGuideline,
  PovertyGuidelineRegion,
} from "../constants/federalPovertyGuidelines";
import { formatCurrency } from "../help/Utils";

const regions = Object.entries(FEDERAL_POVERTY_GUIDELINES) as Array<
  [PovertyGuidelineRegion, (typeof FEDERAL_POVERTY_GUIDELINES)[PovertyGuidelineRegion]]
>;

const FederalPovertyGuidelineCalculator: React.FC = () => {
  const [familySize, setFamilySize] = useState(1);
  const [income, setIncome] = useState("");
  const [region, setRegion] = useState<PovertyGuidelineRegion>("contiguous");

  const annualIncome = Number(income || 0);
  const guideline = useMemo(
    () => getPovertyGuideline(familySize, region),
    [familySize, region]
  );
  const threshold400 = guideline * 4;
  const incomePercent = threshold400 > 0 ? (annualIncome / guideline) * 100 : 0;
  const isAtOrUnder = annualIncome <= threshold400;
  const difference = Math.abs(threshold400 - annualIncome);

  return (
    <div className="page_main page_main-after-login min-h-screen bg-[#F7F8FA] px-4 py-[120px]">
      <div className="mx-auto w-full max-w-5xl">
        <div className="mb-8">
          <p className="text-[13px] font-bold uppercase tracking-wide text-[#C1804D]">
            USCIS fee support
          </p>
          <h1 className="mt-2 text-[32px] font-bold leading-tight text-[#1B365D] md:text-[44px]">
            400% Federal Poverty Guideline Calculator
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-7 text-[#1B365DCC]">
            Check whether your documented annual household income is at or below
            400% of the Federal Poverty Guidelines for your family size. This
            helps answer the reduced USCIS fee question in your application.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm md:p-8">
            <div className="grid gap-5 md:grid-cols-2">
              <label className="block">
                <span className="text-sm font-semibold text-[#1B365D]">
                  Family size
                </span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={familySize}
                  onChange={(e) =>
                    setFamilySize(Math.max(1, Number(e.target.value || 1)))
                  }
                  className="mt-2 w-full rounded-xl border border-[#CBD5E1] px-4 py-3 text-[#1B365D] outline-none focus:border-[#1B365D] focus:ring-2 focus:ring-[#1B365D1A]"
                />
              </label>

              <label className="block">
                <span className="text-sm font-semibold text-[#1B365D]">
                  Annual household income
                </span>
                <input
                  type="number"
                  min={0}
                  step={100}
                  value={income}
                  onChange={(e) => setIncome(e.target.value)}
                  placeholder="0"
                  className="mt-2 w-full rounded-xl border border-[#CBD5E1] px-4 py-3 text-[#1B365D] outline-none focus:border-[#1B365D] focus:ring-2 focus:ring-[#1B365D1A]"
                />
              </label>
            </div>

            <fieldset className="mt-6">
              <legend className="text-sm font-semibold text-[#1B365D]">
                Where do you live?
              </legend>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                {regions.map(([key, value]) => {
                  const selected = region === key;
                  return (
                    <label
                      key={key}
                      className={`flex cursor-pointer items-center rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                        selected
                          ? "border-[#1B365D] bg-[#EEF4FA] text-[#1B365D]"
                          : "border-[#CBD5E1] bg-white text-[#1B365DCC] hover:border-[#1B365D]"
                      }`}
                    >
                      <input
                        type="radio"
                        name="region"
                        value={key}
                        checked={selected}
                        onChange={() => setRegion(key)}
                        className="mr-3"
                        style={{ accentColor: "#1B365D" }}
                      />
                      {value.label}
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <div className="mt-8 rounded-2xl bg-[#1B365D] p-6 text-white">
              <p className="text-sm font-semibold text-[#F4A261]">
                Result
              </p>
              <h2 className="mt-2 text-[26px] font-bold">
                {isAtOrUnder ? "At or under 400%" : "Over 400%"}
              </h2>
              <p className="mt-3 text-sm leading-6 text-white/85">
                Your income is about{" "}
                <span className="font-bold text-white">
                  {Number.isFinite(incomePercent)
                    ? `${incomePercent.toFixed(1)}%`
                    : "0.0%"}
                </span>{" "}
                of the Federal Poverty Guideline for this family size and
                location.
              </p>
            </div>
          </section>

          <aside className="rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
            <p className="text-sm font-bold uppercase tracking-wide text-[#C1804D]">
              Threshold
            </p>
            <dl className="mt-4 space-y-4">
              <div>
                <dt className="text-sm text-[#64748B]">
                  {FEDERAL_POVERTY_GUIDELINE_YEAR} guideline
                </dt>
                <dd className="text-2xl font-bold text-[#1B365D]">
                  {formatCurrency(guideline)}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-[#64748B]">400% threshold</dt>
                <dd className="text-2xl font-bold text-[#1B365D]">
                  {formatCurrency(threshold400)}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-[#64748B]">Difference</dt>
                <dd className="text-xl font-bold text-[#1B365D]">
                  {formatCurrency(difference)}{" "}
                  <span className="text-sm font-semibold text-[#64748B]">
                    {isAtOrUnder ? "under or equal" : "over"}
                  </span>
                </dd>
              </div>
            </dl>

            <div className="mt-6 rounded-xl border border-[#F4A26166] bg-[#FFF8F0] p-4 text-sm leading-6 text-[#1B365D]">
              Based on the {FEDERAL_POVERTY_GUIDELINE_YEAR} HHS Poverty
              Guidelines. USCIS may require supporting documentation and may
              update eligibility rules, so use this as a guide for the
              questionnaire, not a final USCIS decision.
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default FederalPovertyGuidelineCalculator;
